import { ChevronDown, ChevronRight, Plus, RotateCcw, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { PALETTE, type ColorBy, type GraphSettings } from './graphModel';

export interface GraphControlsProps {
  settings: GraphSettings;
  onChange: (patch: Partial<GraphSettings> | 'reset') => void;
  /** The local graph: depth and direction are offered. */
  local: boolean;
  onClose: () => void;
}

function Section({
  title,
  defaultOpen,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="border-border border-b py-1.5 last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1 rounded px-1 py-1 text-left font-semibold text-muted-foreground text-xs hover:bg-muted"
      >
        {open ? (
          <ChevronDown size={13} aria-hidden="true" />
        ) : (
          <ChevronRight size={13} aria-hidden="true" />
        )}
        {title}
      </button>
      {open && (
        <div className="flex flex-col gap-2 px-1 pt-1.5 pb-1">{children}</div>
      )}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-2 text-foreground text-xs">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-primary"
      />
    </label>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex flex-col gap-0.5 text-foreground text-xs">
      <span className="flex justify-between">
        <span>{label}</span>
        <span className="text-muted-foreground tabular-nums">
          {Number.isInteger(step)
            ? value
            : value.toFixed(step < 0.1 ? 2 : 1)}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
      />
    </label>
  );
}

const inputClass =
  'w-full rounded border border-input bg-background px-2 py-1 text-foreground text-xs placeholder:text-muted-foreground focus:border-ring focus:outline-none';

/** Settings panel: filters, groups, display, forces. */
export function GraphControls({
  settings,
  onChange,
  local,
  onClose,
}: GraphControlsProps) {
  const s = settings;
  const setGroup = (
    i: number,
    patch: Partial<{ query: string; color: string }>,
  ) =>
    onChange({
      groups: s.groups.map((g, j) => (j === i ? { ...g, ...patch } : g)),
    });

  return (
    <div className="flex max-h-full flex-col overflow-hidden rounded-lg border border-border bg-popover/95 shadow-lg backdrop-blur">
      <div className="flex shrink-0 items-center justify-between border-border border-b px-2 py-1.5">
        <span className="font-semibold text-foreground text-xs">
          {t.app.graph.settings}
        </span>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => onChange('reset')}
            title={t.app.graph.reset}
            aria-label={t.app.graph.reset}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <RotateCcw size={13} />
          </button>
          <button
            type="button"
            onClick={onClose}
            title={t.app.graph.close}
            aria-label={t.app.graph.close}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={13} />
          </button>
        </div>
      </div>
      <div className="min-h-0 overflow-y-auto px-2">
        {local && (
          <Section title={t.app.graph.local.title} defaultOpen>
            <Slider
              label={t.app.graph.local.depth}
              value={s.depth}
              min={1}
              max={3}
              step={1}
              onChange={(depth) => onChange({ depth })}
            />
            <label className="flex items-center justify-between gap-2 text-foreground text-xs">
              <span>{t.app.graph.local.direction}</span>
              <select
                value={s.direction}
                onChange={(e) =>
                  onChange({
                    direction: e.target.value as GraphSettings['direction'],
                  })
                }
                className={`${inputClass} w-auto`}
              >
                <option value="both">{t.app.graph.local.both}</option>
                <option value="out">{t.app.graph.local.out}</option>
                <option value="in">{t.app.graph.local.in}</option>
              </select>
            </label>
          </Section>
        )}
        <Section title={t.app.graph.filters.title} defaultOpen>
          <input
            type="search"
            value={s.search}
            onChange={(e) => onChange({ search: e.target.value })}
            placeholder={t.app.graph.filters.search}
            aria-label={t.app.graph.filters.search}
            className={inputClass}
          />
          <p className="text-[11px] text-muted-foreground leading-snug">
            {t.app.graph.filters.hint}
          </p>
          <Toggle
            label={t.app.graph.filters.orphans}
            checked={s.showOrphans}
            onChange={(showOrphans) => onChange({ showOrphans })}
          />
        </Section>
        <Section title={t.app.graph.groups.title} defaultOpen>
          <label className="flex items-center justify-between gap-2 text-foreground text-xs">
            <span>{t.app.graph.groups.colorBy}</span>
            <select
              value={s.colorBy}
              onChange={(e) =>
                onChange({ colorBy: e.target.value as ColorBy })
              }
              className={`${inputClass} w-auto`}
            >
              <option value="folder">{t.app.graph.groups.byFolder}</option>
              <option value="none">{t.app.graph.groups.none}</option>
            </select>
          </label>
          {s.groups.map((g, i) => (
            <div key={i} className="flex items-center gap-1">
              <input
                type="color"
                value={g.color}
                onChange={(e) => setGroup(i, { color: e.target.value })}
                aria-label={t.app.graph.groups.color}
                className="h-6 w-6 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
              />
              <input
                value={g.query}
                onChange={(e) => setGroup(i, { query: e.target.value })}
                placeholder={t.app.graph.groups.query}
                aria-label={t.app.graph.groups.query}
                className={inputClass}
              />
              <button
                type="button"
                onClick={() =>
                  onChange({ groups: s.groups.filter((_, j) => j !== i) })
                }
                aria-label={t.app.graph.groups.remove}
                title={t.app.graph.groups.remove}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {s.groups.length < 12 && (
            <button
              type="button"
              onClick={() =>
                onChange({
                  groups: [
                    ...s.groups,
                    {
                      query: '',
                      color: PALETTE[s.groups.length % PALETTE.length]!,
                    },
                  ],
                })
              }
              className="flex items-center gap-1 self-start rounded px-1.5 py-1 text-primary text-xs hover:bg-muted"
            >
              <Plus size={12} /> {t.app.graph.groups.add}
            </button>
          )}
        </Section>
        <Section title={t.app.graph.display.title}>
          <Toggle
            label={t.app.graph.display.arrows}
            checked={s.arrows}
            onChange={(arrows) => onChange({ arrows })}
          />
          <Toggle
            label={t.app.graph.display.sizeByDegree}
            checked={s.sizeByDegree}
            onChange={(sizeByDegree) => onChange({ sizeByDegree })}
          />
          <Slider
            label={t.app.graph.display.labelZoom}
            value={s.labelZoom}
            min={0.2}
            max={4}
            step={0.1}
            onChange={(labelZoom) => onChange({ labelZoom })}
          />
          <Slider
            label={t.app.graph.display.nodeSize}
            value={s.nodeSize}
            min={0.4}
            max={3}
            step={0.1}
            onChange={(nodeSize) => onChange({ nodeSize })}
          />
          <Slider
            label={t.app.graph.display.linkThickness}
            value={s.linkThickness}
            min={0.2}
            max={4}
            step={0.1}
            onChange={(linkThickness) => onChange({ linkThickness })}
          />
        </Section>
        <Section title={t.app.graph.forces.title}>
          <Slider
            label={t.app.graph.forces.center}
            value={s.centerForce}
            min={0}
            max={1}
            step={0.05}
            onChange={(centerForce) => onChange({ centerForce })}
          />
          <Slider
            label={t.app.graph.forces.repel}
            value={s.repelForce}
            min={0}
            max={20}
            step={0.5}
            onChange={(repelForce) => onChange({ repelForce })}
          />
          <Slider
            label={t.app.graph.forces.link}
            value={s.linkForce}
            min={0}
            max={1}
            step={0.05}
            onChange={(linkForce) => onChange({ linkForce })}
          />
          <Slider
            label={t.app.graph.forces.distance}
            value={s.linkDistance}
            min={10}
            max={300}
            step={5}
            onChange={(linkDistance) => onChange({ linkDistance })}
          />
        </Section>
      </div>
    </div>
  );
}
